import { useEffect, useState } from "react";
import axios from "axios";
import PageHeader from "@/components/marketing/PageHeader";
import Newsletter from "@/components/marketing/Newsletter";
import Reveal from "@/components/Reveal";
import { VANGUARD, IMG } from "@/data/content";
import { API } from "@/lib/tracking";
import { trackEvent } from "@/lib/tracking";
import { Newspaper, ExternalLink, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function absoluteUrl(url = "") {
  if (!url) return "";
  return url.startsWith("http") ? url : `${BACKEND}${url}`;
}

export default function VanguardPage() {
  const [issues, setIssues] = useState([]);
  const [active, setActive] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    trackEvent("page_view", { label: "vanguard" });
    (async () => {
      try {
        const { data } = await axios.get(`${API}/vanguard/issues`);
        const valid = (data || []).filter((i) => i.read_more_url || i.pdf_url);
        const latest = valid.find((i) => i.is_latest) || valid[0] || null;
        setIssues(valid);
        setActive(latest);
        setStatus(valid.length ? "ready" : "error");
      } catch (e) {
        setStatus("error");
      }
    })();
  }, []);

  const readUrl = absoluteUrl(active?.read_more_url || active?.pdf_url || "");
  const coverUrl = absoluteUrl(active?.cover_url || "");
  const activeKicker = active && active.period && active.period !== active.title ? active.period : (active?.issue_no || "");

  return (
    <div data-testid="vanguard-page">
      <PageHeader
        overline="Company News Magazine"
        crumb="The Vanguard"
        title={VANGUARD.title}
        subtitle={VANGUARD.intro}
        image={IMG.vanguardBanner}
      />

      <section className="bg-rc-bg py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="mb-12 text-center">
            <div className="font-mono text-rc-red text-xs uppercase tracking-[0.3em] mb-3">Latest Issue</div>
            <p className="text-rc-brown max-w-2xl mx-auto">
              View the current Vanguard issue through Ranger's external magazine reader.
            </p>
          </Reveal>

          {status === "loading" && (
            <div data-testid="vanguard-loading" className="flex flex-col items-center justify-center py-24 text-rc-brown">
              <Loader2 className="animate-spin mb-3" /> Loading issueâ€¦
            </div>
          )}

          {status === "error" && (
            <div data-testid="vanguard-error" className="text-center py-16 text-rc-brown">
              No issues are available to read right now. Please check back soon.
            </div>
          )}

          {status === "ready" && active && (
            <Reveal delay={0.05} className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-[340px,1fr] gap-10 lg:gap-14 items-start">
                <div className="flex justify-center lg:justify-end">
                  {coverUrl ? (
                    <img src={coverUrl} alt={`${active.title} cover`} className="w-full max-w-[300px] rounded-xl shadow-xl border border-black/10" />
                  ) : (
                    <div className="w-full max-w-[300px] aspect-[0.77] rounded-xl bg-white border border-black/10 flex items-center justify-center text-rc-brown">
                      <Newspaper size={48} />
                    </div>
                  )}
                </div>
                <div className="pt-2">
                  <div className="font-mono text-rc-red text-xs uppercase tracking-[0.25em] mb-3">
                    {activeKicker || "Current Issue"}
                  </div>
                  <h2 className="font-heading font-black text-rc-blue text-[clamp(2.4rem,6vw,5rem)] leading-none mb-6">
                    {active.title}
                  </h2>
                  {active.summary && <p className="text-rc-brown leading-relaxed whitespace-pre-line mb-8 max-w-3xl">{active.summary}</p>}
                  <a
                    href={readUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="vanguard-read-more"
                    onClick={() => trackEvent("cta_click", { label: "vanguard_read_more" })}
                    className="inline-flex w-fit items-center gap-2 bg-rc-red text-white px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wide hover:bg-rc-reddark transition-colors"
                  >
                    Read More <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            </Reveal>
          )}

          {issues.length > 1 && (
            <div className="mt-20">
              <div className="mb-8 text-center">
                <div className="font-mono text-rc-red text-xs uppercase tracking-[0.3em] mb-2">Past Issues</div>
                <h3 className="font-heading font-black text-rc-blue text-3xl">Browse the archive</h3>
              </div>
              <div className="relative overflow-hidden -mx-5 px-5" data-testid="vanguard-archive-rail">
                <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-16 bg-gradient-to-r from-rc-bg to-transparent" />
                <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-16 bg-gradient-to-l from-rc-bg to-transparent" />
                <div className="flex w-max gap-8 animate-[vanguard-marquee_55s_linear_infinite] hover:[animation-play-state:paused]">
                  {[...issues.filter((iss) => iss.id !== active?.id), ...issues.filter((iss) => iss.id !== active?.id)].map((iss, idx) => {
                    const archiveCover = absoluteUrl(iss.cover_url || "");
                    const period = iss.period || iss.issue_no || iss.title;
                    return (
                      <a
                        key={`${iss.id}-${idx}`}
                        href={absoluteUrl(iss.read_more_url || iss.pdf_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackEvent("cta_click", { label: `vanguard_archive_${iss.id}` })}
                        className="group w-[245px] sm:w-[280px] lg:w-[315px] shrink-0"
                      >
                        <div className="font-mono text-rc-red text-xs uppercase tracking-[0.2em] mb-3 text-center min-h-[1rem]">{period}</div>
                        <div className="bg-white rounded-2xl border border-black/10 p-3 shadow-md group-hover:border-rc-red group-hover:shadow-xl transition-all">
                          {archiveCover ? (
                            <img src={archiveCover} alt={`${iss.title} cover`} className="w-full aspect-[0.77] object-cover rounded-xl bg-white" />
                          ) : (
                            <div className="w-full aspect-[0.77] rounded-xl bg-rc-charcoal/5 flex items-center justify-center text-rc-brown"><Newspaper size={40} /></div>
                          )}
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="bg-rc-charcoal py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="flex items-center gap-3 text-rc-sand font-mono text-xs uppercase tracking-[0.3em] mb-5">
              <Newspaper size={16} /> Inside Every Issue
            </div>
            <h2 className="font-heading font-black text-white text-[clamp(1.8rem,4vw,3rem)] leading-tight">
              Stories from the field, straight from the crew.
            </h2>
            <ul className="mt-7 space-y-3">
              {VANGUARD.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-white/75">
                  <span className="w-1.5 h-1.5 bg-rc-red rounded-full mt-2.5 shrink-0" /> {h}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1} className="overflow-hidden rounded-2xl">
            <img src={IMG.ellison} alt="The Vanguard News Magazine" className="w-full h-[380px] object-cover" />
          </Reveal>
        </div>
      </section>

      <Newsletter />
    </div>
  );
}

